import { useState, useCallback, useMemo, useEffect } from 'react';
import Modal from './Modal';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { useTranslation } from '../i18n';
import { calendars } from '../api/index';
import { HOLIDAYS_PROJECT_ID } from '../utils/constants';
import './ICalSettingsModal.scss';

/**
 * ICalSettingsModal - configure iCal feed for a calendar
 * Includes project selection similar to ICalExportModal
 */
function ICalSettingsModal({ isOpen, onClose }) {
  const { currentCalendar, updateCalendar, data } = useCalendarDataContext();
  const { t } = useTranslation();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState(new Set());

  // Sort projects: Holidays first, then rest in original order
  const sortedProjects = useMemo(() => {
    const allProjects = data?.projects || [];
    const holidayIndex = allProjects.findIndex(p => p.isSystem || p.id === HOLIDAYS_PROJECT_ID);
    
    if (holidayIndex === -1 || holidayIndex === 0) {
      return allProjects;
    }
    
    const sorted = [...allProjects];
    const [holidayProject] = sorted.splice(holidayIndex, 1);
    sorted.unshift(holidayProject);
    return sorted;
  }, [data?.projects]);

  // Initialize selection from calendar settings or default to all projects
  useEffect(() => {
    if (!currentCalendar || !sortedProjects.length) return;
    
    if (currentCalendar.icalProjectIds) {
      // Use saved selection
      try {
        const ids = typeof currentCalendar.icalProjectIds === 'string' 
          ? JSON.parse(currentCalendar.icalProjectIds)
          : currentCalendar.icalProjectIds;
        setSelectedProjects(new Set(ids));
      } catch {
        // Invalid JSON, select all
        setSelectedProjects(new Set(sortedProjects.map(p => p.id)));
      }
    } else {
      // No selection saved, default to all visible projects
      setSelectedProjects(new Set(sortedProjects.filter(p => p.visible).map(p => p.id)));
    }
  }, [currentCalendar, sortedProjects]);

  const icalUrl = currentCalendar?.icalToken 
    ? calendars.getIcalUrl(currentCalendar.icalToken)
    : null;

  const handleToggleEnabled = async () => {
    if (!currentCalendar) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      await updateCalendar(currentCalendar.id, {
        icalEnabled: !currentCalendar.icalEnabled,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleProject = (projectId) => {
    setSelectedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedProjects(new Set(sortedProjects.map(p => p.id)));
  };

  const handleDeselectAll = () => {
    setSelectedProjects(new Set());
  };

  const handleSaveProjectSelection = async () => {
    if (!currentCalendar) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const selectedIds = Array.from(selectedProjects);
      await updateCalendar(currentCalendar.id, {
        icalProjectIds: selectedIds.length === sortedProjects.length ? null : selectedIds,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!currentCalendar) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      await calendars.regenerateIcalToken(currentCalendar.id);
      // Refresh calendar data to get new token
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopyUrl = useCallback(async () => {
    if (!icalUrl) return;
    
    try {
      await navigator.clipboard.writeText(icalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = icalUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [icalUrl]);

  // Check if selection changed from saved state
  const hasSelectionChanged = useMemo(() => {
    if (!currentCalendar) return false;
    
    const savedIds = currentCalendar.icalProjectIds 
      ? (typeof currentCalendar.icalProjectIds === 'string' 
          ? JSON.parse(currentCalendar.icalProjectIds) 
          : currentCalendar.icalProjectIds)
      : sortedProjects.map(p => p.id);
    
    const savedSet = new Set(savedIds);
    if (savedSet.size !== selectedProjects.size) return true;
    for (const id of selectedProjects) {
      if (!savedSet.has(id)) return true;
    }
    return false;
  }, [currentCalendar, selectedProjects, sortedProjects]);

  if (!currentCalendar) {
    return null;
  }

  const hasSelection = selectedProjects.size > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('ical.title')} maxWidth={440}>
      <div className="ical-settings">
        <p className="ical-settings__description">
          {t('ical.description')}
        </p>

        <div className="ical-settings__toggle">
          <label className="ical-settings__toggle-label">
            <input
              type="checkbox"
              checked={currentCalendar.icalEnabled || false}
              onChange={handleToggleEnabled}
              disabled={isProcessing}
            />
            <span>{t('ical.enableFeed')}</span>
          </label>
        </div>

        {currentCalendar.icalEnabled && (
          <>
            {/* Project selection */}
            <div className="ical-settings__section">
              <label className="ical-settings__section-label">{t('icalExport.selectProjects')}</label>
              <div className="ical-settings__projects">
                {sortedProjects.map(project => {
                  const isChecked = selectedProjects.has(project.id);
                  const isHolidays = project.isSystem || project.id === HOLIDAYS_PROJECT_ID;
                  const displayName = isHolidays ? t('project.holidaysName') : project.name;
                  const hiddenLabel = !project.visible ? ` ${t('icalExport.hiddenSuffix')}` : '';
                  
                  return (
                    <label
                      key={project.id}
                      className={`ical-settings__project ${!project.visible ? 'ical-settings__project--hidden' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleProject(project.id)}
                        disabled={isProcessing}
                      />
                      <span
                        className="ical-settings__project-color"
                        style={{ backgroundColor: project.color }}
                      />
                      <span className="ical-settings__project-name">
                        {displayName}{hiddenLabel}
                      </span>
                    </label>
                  );
                })}
              </div>
              
              <div className="ical-settings__select-actions">
                <button
                  type="button"
                  className="ical-settings__select-btn"
                  onClick={handleSelectAll}
                  disabled={isProcessing}
                >
                  {t('icalExport.selectAll')}
                </button>
                <button
                  type="button"
                  className="ical-settings__select-btn"
                  onClick={handleDeselectAll}
                  disabled={isProcessing}
                >
                  {t('icalExport.deselectAll')}
                </button>
                {hasSelectionChanged && (
                  <button
                    type="button"
                    className="ical-settings__save-btn"
                    onClick={handleSaveProjectSelection}
                    disabled={isProcessing || !hasSelection}
                  >
                    {t('common.save')}
                  </button>
                )}
              </div>
            </div>

            {/* URL section */}
            {icalUrl && (
              <div className="ical-settings__url-section">
                <label className="ical-settings__section-label">{t('ical.feedUrl')}</label>
                <div className="ical-settings__url-row">
                  <input
                    type="text"
                    value={icalUrl}
                    readOnly
                    className="ical-settings__url-input"
                  />
                  <button
                    className="ical-settings__copy-btn"
                    onClick={handleCopyUrl}
                    disabled={isProcessing}
                  >
                    {copied ? t('ical.copied') : t('ical.copy')}
                  </button>
                </div>
                
                <p className="ical-settings__hint">
                  {t('ical.urlHint')}
                </p>

                <button
                  className="ical-settings__regenerate"
                  onClick={handleRegenerateToken}
                  disabled={isProcessing}
                >
                  {t('ical.regenerateToken')}
                </button>
                <p className="ical-settings__warning">
                  {t('ical.regenerateWarning')}
                </p>
              </div>
            )}
          </>
        )}

        {error && (
          <div className="ical-settings__error">{error}</div>
        )}
      </div>
    </Modal>
  );
}

export default ICalSettingsModal;
